import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Stage, Layer, Line, Circle, Rect } from "react-konva";
import { validateReport } from "./validators/reportValidator";
import "components/shared/occurrence-modal.css";

const COLORS = [
  { label: "Azul",     value: "#035184" },
  { label: "Vermelho", value: "#dc2626" },
  { label: "Amarelo",  value: "#f59e0b" },
  { label: "Verde",    value: "#16a34a" },
];

const STROKE_WIDTH = 3;

const ReportModal = ({ screenshot, onClose, onAdded }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const divRef = useRef(null);
  const stageRef = useRef(null);

  const [tool, setTool] = useState("pen");
  const [strokeColor, setStrokeColor] = useState("#035184");
  const [lines, setLines] = useState([]);
  const [circles, setCircles] = useState([]);
  const [rects, setRects] = useState([]);
  const [previewShape, setPreviewShape] = useState(null);
  const isDrawing = useRef(false);
  const shapeStart = useRef(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue
  } = useForm();

  const selectedSeverity = watch("severity");

  useEffect(() => {
    if (!screenshot) return;
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = screenshot;
  }, [screenshot]);

  useEffect(() => {
    if (!imageLoaded || !divRef.current) return;
    const w = divRef.current.clientWidth;
    const h = divRef.current.clientHeight;
    setDimensions({ width: w, height: h });
  }, [imageLoaded]);

  const handleMouseDown = e => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;
    if (tool === "pen") {
      isDrawing.current = true;
      setLines(prev => [...prev, { points: [pos.x, pos.y], stroke: strokeColor }]);
    } else if (tool === "circle" || tool === "rect") {
      shapeStart.current = { x: pos.x, y: pos.y };
    }
  };

  const handleMouseMove = e => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;
    if (tool === "pen" && isDrawing.current && lines.length > 0) {
      const last = lines[lines.length - 1];
      last.points = last.points.concat([pos.x, pos.y]);
      setLines([...lines.slice(0, -1), last]);
    } else if ((tool === "circle" || tool === "rect") && shapeStart.current) {
      const start = shapeStart.current;
      if (tool === "circle") {
        const radius = Math.sqrt(
          Math.pow(pos.x - start.x, 2) + Math.pow(pos.y - start.y, 2)
        );
        setPreviewShape({ type: "circle", x: start.x, y: start.y, radius });
      } else {
        const x = Math.min(start.x, pos.x);
        const y = Math.min(start.y, pos.y);
        const width = Math.abs(pos.x - start.x);
        const height = Math.abs(pos.y - start.y);
        setPreviewShape({ type: "rect", x, y, width, height });
      }
    }
  };

  const handleMouseUp = () => {
    if (tool === "pen") {
      isDrawing.current = false;
    } else if ((tool === "circle" || tool === "rect") && shapeStart.current && previewShape) {
      if (previewShape.type === "circle" && previewShape.radius > 2) {
        setCircles(prev => [
          ...prev,
          { x: previewShape.x, y: previewShape.y, radius: previewShape.radius, stroke: strokeColor }
        ]);
      } else if (previewShape.type === "rect" && previewShape.width > 2 && previewShape.height > 2) {
        setRects(prev => [
          ...prev,
          { x: previewShape.x, y: previewShape.y, width: previewShape.width, height: previewShape.height, stroke: strokeColor }
        ]);
      }
      shapeStart.current = null;
      setPreviewShape(null);
    }
  };

  const handleClearAll = () => {
    setLines([]);
    setCircles([]);
    setRects([]);
    setPreviewShape(null);
    shapeStart.current = null;
  };

  const handleUndo = () => {
    if (lines.length > 0) setLines(prev => prev.slice(0, -1));
    else if (circles.length > 0) setCircles(prev => prev.slice(0, -1));
    else if (rects.length > 0) setRects(prev => prev.slice(0, -1));
  };

  const hasDrawings = lines.length > 0 || circles.length > 0 || rects.length > 0;

  const onSubmit = () => {
    const values = getValues();
    const validationErrors = validateReport(values);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, message]) => {
        setError(field, { message });
      });
      return;
    }

    try {
      const stage = stageRef.current;
      const stageDataUrl = stage.toDataURL();
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = dimensions.width;
      tempCanvas.height = dimensions.height;
      const ctx = tempCanvas.getContext("2d");

      const bgImg = new Image();
      bgImg.src = screenshot;
      const drawAndSave = () => {
        const nw = bgImg.naturalWidth;
        const nh = bgImg.naturalHeight;
        const cw = dimensions.width;
        const ch = dimensions.height;
        const imgAspect = nw / nh;
        const containerAspect = cw / ch;
        let dw, dh, dx, dy;
        if (imgAspect > containerAspect) {
          dw = cw; dh = cw / imgAspect; dx = 0; dy = (ch - dh) / 2;
        } else {
          dh = ch; dw = ch * imgAspect; dx = (cw - dw) / 2; dy = 0;
        }
        ctx.fillStyle = "#f8fafc";
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(bgImg, 0, 0, nw, nh, dx, dy, dw, dh);
        const stageImg = new Image();
        stageImg.src = stageDataUrl;
        stageImg.onload = () => {
          ctx.drawImage(stageImg, 0, 0, dimensions.width, dimensions.height);
          const combinedDataUrl = tempCanvas.toDataURL();
          const newReport = { ...values, image: combinedDataUrl };
          const existingReports = JSON.parse(localStorage.getItem("reports") || "[]");
          localStorage.setItem("reports", JSON.stringify([...existingReports, newReport]));
          reset();
          onClose();
          onAdded?.();
        };
      };
      if (bgImg.complete) drawAndSave();
      else bgImg.onload = drawAndSave;
    } catch (e) {
      setError("title", { message: "Erro ao salvar. Libere espaço e tente novamente." });
    }
  };

  if (!screenshot) return null;

  return (
    <div className="occ-overlay">
      <div className="occ-modal">
        <div className="occ-modal__header">
          <div className="occ-modal__header-inner">
            <span className="occ-modal__accent">Ocorrência</span>
            <h2 className="occ-modal__title">Registro de Ocorrência</h2>
            <p className="occ-modal__subtitle">Anote na imagem e preencha os dados abaixo.</p>
          </div>
          <button
            type="button"
            className="occ-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="occ-modal__body">
          <div className="occ-modal__canvas-wrap">
            <div ref={divRef} className="occ-modal__canvas-area">
              <img
                src={screenshot}
                alt="Screenshot"
                className="occ-modal__img"
                onLoad={() => setImageLoaded(true)}
              />
              {imageLoaded && dimensions.width > 0 && dimensions.height > 0 && (
                <Stage
                  ref={stageRef}
                  width={dimensions.width}
                  height={dimensions.height}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className={`occ-modal__stage occ-modal__stage--${tool}`}
                  style={{ position: "absolute", top: 0, left: 0, borderRadius: "10px" }}
                >
                  <Layer>
                    {lines.map((line, i) => (
                      <Line
                        key={`line-${i}`}
                        points={line.points}
                        stroke={line.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                      />
                    ))}
                    {circles.map((c, i) => (
                      <Circle
                        key={`circle-${i}`}
                        x={c.x} y={c.y}
                        radius={c.radius}
                        stroke={c.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                      />
                    ))}
                    {rects.map((r, i) => (
                      <Rect
                        key={`rect-${i}`}
                        x={r.x} y={r.y}
                        width={r.width} height={r.height}
                        stroke={r.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                      />
                    ))}
                    {previewShape?.type === "circle" && (
                      <Circle
                        x={previewShape.x} y={previewShape.y}
                        radius={previewShape.radius}
                        stroke={strokeColor}
                        strokeWidth={STROKE_WIDTH}
                        listening={false}
                      />
                    )}
                    {previewShape?.type === "rect" && (
                      <Rect
                        x={previewShape.x} y={previewShape.y}
                        width={previewShape.width} height={previewShape.height}
                        stroke={strokeColor}
                        strokeWidth={STROKE_WIDTH}
                        listening={false}
                      />
                    )}
                  </Layer>
                </Stage>
              )}
            </div>

            <div className="occ-modal__tools">
              <span className="occ-modal__tools-label">Anotar:</span>
              <div className="occ-modal__tools-btns">
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "pen" ? "active" : ""}`}
                  onClick={() => setTool("pen")}
                  title="Caneta"
                >
                  <i className="fas fa-pencil-alt" />
                </button>
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "circle" ? "active" : ""}`}
                  onClick={() => setTool("circle")}
                  title="Círculo"
                >
                  <i className="fas fa-circle" />
                </button>
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "rect" ? "active" : ""}`}
                  onClick={() => setTool("rect")}
                  title="Retângulo"
                >
                  <i className="fas fa-square" />
                </button>
              </div>
              <div className="occ-modal__tools-color">
                <span className="occ-modal__tools-label">Cor:</span>
                <div className="occ-modal__color-select-wrap">
                  <span
                    className="occ-modal__color-swatch-preview"
                    style={{ backgroundColor: strokeColor }}
                  />
                  <select
                    className="occ-modal__color-select"
                    value={strokeColor}
                    onChange={e => setStrokeColor(e.target.value)}
                  >
                    {COLORS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="occ-modal__tools-actions">
                <button
                  type="button"
                  className="occ-modal__tool-btn"
                  onClick={handleUndo}
                  disabled={!hasDrawings}
                  title="Desfazer"
                >
                  <i className="fas fa-undo" />
                </button>
                <button
                  type="button"
                  className="occ-modal__tool-btn occ-modal__tool-btn--danger"
                  onClick={handleClearAll}
                  disabled={!hasDrawings}
                  title="Apagar tudo"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              </div>
            </div>
          </div>

          <form className="occ-modal__form-wrap" onSubmit={handleSubmit(onSubmit)}>
            <div className="occ-modal__form-group">
              <label className="occ-modal__form-label">Título</label>
              <input
                {...register("title")}
                className={`occ-modal__form-input ${errors.title ? "error" : ""}`}
                placeholder="Ex.: Trinca na parede norte"
                onChange={e => setValue("title", e.target.value)}
              />
              {errors.title && (
                <span className="occ-modal__form-error">{errors.title.message}</span>
              )}
            </div>

            <div className="occ-modal__form-group">
              <label className="occ-modal__form-label">Descrição</label>
              <textarea
                {...register("description")}
                className={`occ-modal__form-input occ-modal__form-textarea ${errors.description ? "error" : ""}`}
                placeholder="Descreva a ocorrência"
                onChange={e => setValue("description", e.target.value)}
              />
              {errors.description && (
                <span className="occ-modal__form-error">{errors.description.message}</span>
              )}
            </div>

            <div className="occ-modal__form-group">
              <label className="occ-modal__form-label">Ação recomendada</label>
              <textarea
                {...register("action")}
                className={`occ-modal__form-input occ-modal__form-textarea ${errors.action ? "error" : ""}`}
                placeholder="Descreva a ação a ser tomada"
                onChange={e => setValue("action", e.target.value)}
              />
              {errors.action && (
                <span className="occ-modal__form-error">{errors.action.message}</span>
              )}
            </div>

            <div className="occ-modal__form-group">
              <label className="occ-modal__form-label">Severidade</label>
              <div className="occ-modal__severidade">
                {["alta", "media", "baixa"].map(value => (
                  <label
                    key={value}
                    className={`occ-modal__severidade-option ${
                      selectedSeverity === value ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="severity"
                      value={value}
                      {...register("severity")}
                      onChange={e => setValue("severity", e.target.value)}
                      className="occ-sr-only"
                    />
                    <span>
                      {value === "alta" ? "Alta" : value === "media" ? "Média" : "Baixa"}
                    </span>
                  </label>
                ))}
              </div>
              {errors.severity && (
                <span className="occ-modal__form-error">{errors.severity.message}</span>
              )}
            </div>

            <div className="occ-modal__footer" style={{ padding: "1rem 0 0", borderTop: "none" }}>
              <button type="submit" className="occ-modal__submit">
                Adicionar ao Relatório
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
